// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import {
  getCreateAccountPayload,
  setLoyaltyLocation,
  getLoyaltyLocation,
} from '@tcp/core/src/constants/analytics';
import ClickTracker from '@tcp/web/src/components/common/atoms/ClickTracker';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import { routerPush, isMobileWeb, isAppleDevice } from '@tcp/core/src/utils';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import LoginForm from '../../../molecules/LoginForm';
import LoginTopSectionV1 from '../../../molecules/LoginTopSection';
import LoginPasswordSection from '../../LoginPasswordSection';
import EmailForm from '../../../molecules/EmailForm';
import Row from '../../../../../../common/atoms/Row';
import Col from '../../../../../../common/atoms/Col';
import Button from '../../../../../../common/atoms/Button';
import styles from './styles/LoginSection.styles';
import constants from '../../../LoginPage.constants';
import { isCanada, scrollPage, getUrlParameter, getViewportInfo } from '../../../../../../../utils';
import LoginOTPSection from '../../LoginOTPSection';
import LoginHelloScreenV2 from '../../LoginHelloScreen/views/LoginHelloScreenV2.view';
import GuestFavMessaging from './GuestFavMessaging.view';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '../../../../../../../utils/labels/labelUtilty';
import {
  GlobalActionsLabels,
  GLOBAL_ACTIONS_LABELS,
  lblCreateAccount,
} from '../../../../../../../utils/labels/globalActions.labels';
import {
  GLOBAL_LOGIN_LABELS,
  GlobalLoginLabels,
} from '../../../../../../../utils/labels/globalLogin.labels';
import CreateAccountOtpForm from '../../../molecules/CreateAccountOtpForm/views/CreateAccountOtpForm.view';
import ResetPassword from '../../../../ResetPassword';
import ForgotPasswordContainer from '../../../../ForgotPassword/container/ForgotPassword.container';

const LogOutPageContainer = dynamic(() =>
  // eslint-disable-next-line extra-rules/no-commented-out-code
  import(/* webpackChunkName: "logout-page" */ '../../../../Logout/container/LogOut.container')
);

class LoginSection extends React.PureComponent {
  constructor(props) {
    super(props);
    this.isCanada = isCanada();
    this.parentRef = React.createRef();
    this.state = {
      redirectFromReset: false,
      rememberUser: true,
      newCodeSent: false,
    };
  }

  componentDidMount() {
    const { loginFlowUXEnhancementEnabled } = this.props;
    const redirectFromReset = getUrlParameter('redirectFromReset');
    if (redirectFromReset && redirectFromReset.length > 0) {
      this.setState({ redirectFromReset: true });
      routerPush('/home?target=login', '/home/login', { shallow: true });
    }
    if (this.parentRef && this.parentRef.current && !loginFlowUXEnhancementEnabled) {
      setTimeout(() => {
        this.parentRef.current.scrollIntoView();
      }, 0);
    } else {
      window?.scrollTo(0, 0);
    }
  }

  componentDidUpdate(prevProps) {
    const { currentForm, loginFlowUXEnhancementEnabled } = this.props;
    if (currentForm !== prevProps.currentForm && !loginFlowUXEnhancementEnabled) {
      scrollPage();
    } else if (!(isMobileWeb() && isAppleDevice())) {
      window?.scrollTo(0, 0);
    }
  }

  showForgotPasswordForm = () => {
    const { openModal } = this.props;
    openModal({
      component: 'login',
      componentProps: {
        currentForm: constants.PAGE_TYPE.FORGOT_PASSWORD,
      },
    });
  };

  showLoginForm = () => {
    const { openModal } = this.props;
    openModal({
      component: 'login',
      componentProps: {
        currentForm: constants.PAGE_TYPE.LOGIN,
      },
    });
  };

  showCreateAccountForm = () => {
    const {
      onClose,
      openOverlay,
      closeModal,
      fullPage,
      closeOverlay,
      fullPageAuthEnabled,
      variation,
    } = this.props;
    const { pathname = 'referrer' } = (window && window.location) || {};
    if (variation !== 'favorites' && (fullPage || fullPageAuthEnabled)) {
      closeOverlay();
      routerPush(`/home?target=register&successtarget=${pathname}`, '/home/register');
    } else {
      openOverlay({
        component: 'createAccount',
        variation: 'primary',
      });
    }
    onClose();
    closeModal();
  };

  getSignOutSection = (isRememberedUser, logoutlabels, userName, lblLoginNot) => {
    return isRememberedUser ? (
      <BodyCopy component="div" className="elem-pb-LRG">
        <BodyCopy fontFamily="primary" fontSize="fs14">
          {`${lblLoginNot} ${userName} ? `}
          <Anchor
            underline
            fontSizeVariation="medium"
            anchorVariation="primary"
            onClick={this.showLoginForm}
          >
            <LogOutPageContainer labels={logoutlabels} underline />
          </Anchor>
        </BodyCopy>
      </BodyCopy>
    ) : null;
  };

  getCreateAccountHelp = (
    fullPage,
    lblLoginCreateAcctHelp,
    lblCreateAcntHelp1v1,
    lblCreateAcntHelp2v1
  ) => {
    return !fullPage ? (
      <BodyCopy fontFamily="secondary" fontSize="fs12" textAlign="center">
        {lblLoginCreateAcctHelp}
      </BodyCopy>
    ) : (
      <>
        <BodyCopy fontFamily="secondary" fontSize="fs12" textAlign="center">
          {lblCreateAcntHelp1v1}
        </BodyCopy>
        <BodyCopy fontFamily="secondary" fontSize="fs12" textAlign="center">
          {lblCreateAcntHelp2v1}
        </BodyCopy>
      </>
    );
  };

  getLoyaltyLocation = (variation) => {
    if (variation === 'favorites') {
      return getLoyaltyLocation();
    }
    return variation === 'checkout' ? 'checkout-popup' : 'Login';
  };

  onEmailButtonClick = (payload) => {
    const { onEmailSubmit } = this.props;
    onEmailSubmit(payload);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  getEmailSectionContainer = () => {
    const {
      isExistingUserFromSS,
      isRememberedUser,
      existingUserResponse,
      isShowOTPInputScreen,
      variation,
      emailAddressFromSS,
      isOTpScreenEnabled,
      isOtpLimit,
      isLocked,
      resetOtpLimit,
      resetIsLocked,
    } = this.props;
    const emailSectionProps = { ...this.props, onSubmit: this.onEmailButtonClick };
    const { emailAddress, otpLimit, locked, vstatus, lockTime } = existingUserResponse || {};
    const { isMobile } = getViewportInfo() || {};
    const showCreateAccOtpScreen = vstatus !== undefined && !vstatus;

    return (
      <>
        {showCreateAccOtpScreen && (otpLimit || locked) && isOTpScreenEnabled ? (
          <div>
            <EmailForm
              {...emailSectionProps}
              otpLimit={isOtpLimit}
              isLocked={isLocked}
              resetOtpLimit={resetOtpLimit}
              resetIsLocked={resetIsLocked}
              lockTime={lockTime}
            />
          </div>
        ) : (
          <>
            {!isShowOTPInputScreen && (
              <>
                {(isExistingUserFromSS && emailAddress) || isRememberedUser ? (
                  <>
                    <LoginPasswordSection {...this.props} setRememberUser={this.setRememberUser} />
                  </>
                ) : (
                  <>
                    {!isExistingUserFromSS && emailAddressFromSS && variation === 'checkout' ? (
                      <LoginHelloScreenV2 {...this.props} />
                    ) : (
                      <div className={!isMobile && 'elem-pl-LRG elem-pr-LRG'}>
                        <EmailForm {...emailSectionProps} />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </>
    );
  };

  generateOTP = (payload) => {
    const { onGenerateOTP, otpMessageThreshold, loginErrorMessage } = this.props;
    onGenerateOTP({ isResend: true, ...payload });
    if (!loginErrorMessage && loginErrorMessage === '') {
      this.setState({ newCodeSent: true });
    }
    const timer = setTimeout(() => {
      this.setState({ newCodeSent: false });
      clearTimeout(timer);
    }, otpMessageThreshold);
  };

  onOTPVerify = (values) => {
    const { onSubmit, existingUserResponse } = this.props;
    const { rememberMe, emailAddress } = existingUserResponse;

    const payload = { ...values, emailAddress, rememberMe };
    onSubmit(payload);
  };

  getEmailSection = () => {
    const { newCodeSent } = this.state;

    const otpSectionProps = {
      ...this.props,
      newCodeSent,
      onSubmit: this.onOTPVerify,
      generateOTP: this.generateOTP,
    };
    let isFromCheckout;

    if (window?.location?.href.includes('from=SA')) {
      isFromCheckout = false;
    } else if (window?.location?.href.includes('register-checkout')) {
      isFromCheckout = true;
    } else {
      isFromCheckout = false;
    }

    const {
      existingUserResponse,
      isShowOTPInputScreen,
      variation,
      isOTpScreenEnabled,
      loginErrorMessage,
      resetLoginState,
      routeForBagCheckout,
      otpToken,
      setIsFromCreateAccount,
      isLoading,
      setLoader,
      saveExistingUserResponse,
      sessionStorageUserInfo,
      showRecaptcha,
      setShowVerificationErrorAction,
    } = this.props;
    const { emailAddress } = existingUserResponse || {};
    const {
      vstatus: verificationStatus,
      otpLimit,
      token,
      locked,
      lockTime,
    } = sessionStorageUserInfo || {};

    const { rememberUser } = this.state;

    const showCreateAccOtpScreen = verificationStatus !== undefined && !verificationStatus;

    return (
      <div className={`${variation !== 'checkout' && 'loginPageSpacing'}`}>
        {isShowOTPInputScreen && (
          <>
            <LoginOTPSection
              {...this.props}
              rememberUser={rememberUser}
              emailAddress={emailAddress}
            />
          </>
        )}
        {showCreateAccOtpScreen && isOTpScreenEnabled && emailAddress && !otpLimit && !locked ? (
          <CreateAccountOtpForm
            emailAddress={existingUserResponse?.emailAddress || emailAddress}
            isFromCheckout={isFromCheckout}
            routeForBagCheckout={routeForBagCheckout}
            saveExistingUserResponse={saveExistingUserResponse}
            loginErrorMessage={loginErrorMessage}
            resetLoginState={resetLoginState}
            otpToken={otpToken || token}
            setIsFromCreateAccount={setIsFromCreateAccount}
            isLoginLoading={isLoading}
            setLoader={setLoader}
            showRecaptcha={showRecaptcha}
            setShowVerificationErrorAction={setShowVerificationErrorAction}
            lockTime={lockTime}
            {...otpSectionProps}
          />
        ) : (
          this.getEmailSectionContainer()
        )}
      </div>
    );
  };

  setRememberUser = (e) => {
    if (e?.target) {
      this.setState({ rememberUser: e?.target?.checked });
    } else {
      this.setState({ rememberUser: true });
    }
  };

  render() {
    const {
      onSubmit,
      loginErrorMessage,
      initialValues,
      showRecaptcha,
      resetForm,
      className,
      queryParams,
      currentForm,
      variation,
      handleContinueAsGuest,
      tooltipContent,
      resetLoginState,
      userplccCardNumber,
      userplccCardId,
      isLoading,
      isRememberedUser,
      closeOverlay,
      isResetPassCodeValidationEnabled,
      closeModal,
      fullPage,
      setUserEmailAddress,
      isLoginWithOTPEnabled,
      fromGuestFav,
      guestFavMessages,
      logoutlabels,
      userName,
    } = this.props;

    const {
      lbl_login_not: lblLoginNot,
      lbl_login_createAccountHelp: lblLoginCreateAcctHelp,
      lbl_login_createAccountHelp_1_v1: lblCreateAcntHelp1v1,
      lbl_login_createAccountHelp_2_v1: lblCreateAcntHelp2v1,
    } = getLabelFromCopyOrFromTranslation(GLOBAL_LOGIN_LABELS, GlobalLoginLabels);

    const { redirectFromReset } = this.state;
    return (
      <>
        {fromGuestFav && <GuestFavMessaging guestFavMessages={guestFavMessages} />}
        <div className={className} ref={this.parentRef}>
          {(!currentForm || currentForm === constants.PAGE_TYPE.LOGIN) &&
            !isLoginWithOTPEnabled && (
              <LoginTopSectionV1
                variation={variation}
                className="elem-mb-SM elem-mt-MED"
                isCanada={this.isCanada}
                resetLoginState={resetLoginState}
                showForgotPasswordForm={this.showForgotPasswordForm}
                isRememberedUser={isRememberedUser}
                closeOverlay={closeOverlay}
                closeModal={closeModal}
                isResetPassCodeValidationEnabled={isResetPassCodeValidationEnabled}
                redirectFromReset={redirectFromReset}
                fullPage={fullPage}
                setUserEmailAddress={setUserEmailAddress}
              />
            )}
          <Row>
            <Col
              colSize={{
                small: 6,
                medium: 8,
                large: `${variation === 'checkout' ? 7 : 12}`,
              }}
              className={`elem-pt-SM ${!isLoginWithOTPEnabled && 'elem-pl-LRG elem-pr-LRG'} ${
                variation === 'checkout' ? 'checkoutForm' : 'loginForm'
              }`}
            >
              {isLoginWithOTPEnabled && <>{this.getEmailSection()}</>}
              {!isLoginWithOTPEnabled && (
                <>
                  {(!currentForm || currentForm === constants.PAGE_TYPE.LOGIN) && (
                    <React.Fragment>
                      <LoginForm
                        onSubmit={onSubmit}
                        loginErrorMessage={loginErrorMessage}
                        initialValues={initialValues}
                        showRecaptcha={showRecaptcha}
                        isRememberedUser={isRememberedUser}
                        showForgotPasswordForm={this.showForgotPasswordForm}
                        resetForm={resetForm}
                        className="elem-mb-LRG"
                        onCreateAccountClick={this.showCreateAccountForm}
                        variation={variation}
                        handleContinueAsGuest={handleContinueAsGuest}
                        tooltipContent={tooltipContent}
                        resetLoginState={resetLoginState}
                        userplccCardNumber={userplccCardNumber}
                        userplccCardId={userplccCardId}
                        isLoading={isLoading}
                        closeOverlay={closeOverlay}
                        closeModal={closeModal}
                        isResetPassCodeValidationEnabled={isResetPassCodeValidationEnabled}
                        fullPage={fullPage}
                        setUserEmailAddress={setUserEmailAddress}
                      />
                    </React.Fragment>
                  )}
                  {currentForm === constants.PAGE_TYPE.FORGOT_PASSWORD && (
                    <ForgotPasswordContainer showForgotPasswordForm={this.showLoginForm} />
                  )}
                  {currentForm === constants.PAGE_TYPE.RESET_PASSWORD && (
                    <ResetPassword
                      backToLoginAction={this.showLoginForm}
                      queryParams={queryParams}
                    />
                  )}
                </>
              )}
              {!isRememberedUser && !isLoginWithOTPEnabled && (
                <>
                  <BodyCopy component="div" className="border elem-pt-MED elem-pb-LRG">
                    {this.getCreateAccountHelp(
                      fullPage,
                      lblLoginCreateAcctHelp,
                      lblCreateAcntHelp1v1,
                      lblCreateAcntHelp2v1
                    )}
                  </BodyCopy>
                  <ClickTracker
                    onClick={() => {
                      setLoyaltyLocation(this.getLoyaltyLocation(variation));
                    }}
                    clickData={getCreateAccountPayload(this.getLoyaltyLocation(variation))}
                  >
                    <Button
                      className="create-acc-cta"
                      fill="WHITE"
                      type="submit"
                      buttonVariation="fixed-width"
                      data-locator="create-acc-cta"
                      onClick={this.showCreateAccountForm}
                      spc={variation === 'checkout'}
                    >
                      {renderLabel(
                        getLabelFromCopyOrFromTranslation(
                          `${GLOBAL_ACTIONS_LABELS}.${lblCreateAccount}`,
                          GlobalActionsLabels
                        ),
                        'lbl_createAccount'
                      )}
                    </Button>
                  </ClickTracker>
                </>
              )}

              {!isLoginWithOTPEnabled &&
                this.getSignOutSection(isRememberedUser, logoutlabels, userName, lblLoginNot)}
            </Col>
          </Row>
        </div>
      </>
    );
  }
}

LoginSection.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  loginErrorMessage: PropTypes.string,
  initialValues: PropTypes.shape({}).isRequired,
  showRecaptcha: PropTypes.bool,
  openModal: PropTypes.func,
  queryParams: PropTypes.shape({}).isRequired,
  currentForm: PropTypes.string,
  handleContinueAsGuest: PropTypes.func.isRequired,
  userplccCardNumber: PropTypes.string.isRequired,
  userplccCardId: PropTypes.string.isRequired,
  logoutlabels: PropTypes.shape({}),
  onClose: PropTypes.func.isRequired,
  openOverlay: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  resetForm: PropTypes.bool.isRequired,
  className: PropTypes.string.isRequired,
  variation: PropTypes.string.isRequired,
  tooltipContent: PropTypes.string.isRequired,
  resetLoginState: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isRememberedUser: PropTypes.bool.isRequired,
  closeOverlay: PropTypes.func,
  isResetPassCodeValidationEnabled: PropTypes.bool,
  fullPage: PropTypes.bool,
  fullPageAuthEnabled: PropTypes.bool,
  isLoginWithOTPEnabled: PropTypes.bool,
  onEmailSubmit: PropTypes.func,
  trackAnalyticsClick: PropTypes.func,
  existingUserResponse: PropTypes.shape({}),
  emailAddressFromSS: PropTypes.string,
  isExistingUserFromSS: PropTypes.bool,
  SubmitForgot: PropTypes.bool.isRequired,
  setLoginSelectionOption: PropTypes.func,
  selectedPassResetOption: PropTypes.string,
  isV2Flow: PropTypes.bool,
  otpMessageThreshold: PropTypes.number,
};

LoginSection.defaultProps = {
  loginErrorMessage: '',
  showRecaptcha: false,
  openModal: () => {},
  currentForm: constants.PAGE_TYPE.LOGIN,
  closeOverlay: () => {},
  isResetPassCodeValidationEnabled: false,
  fullPage: false,
  fullPageAuthEnabled: false,
  isLoginWithOTPEnabled: false,
  onEmailSubmit: () => {},
  trackAnalyticsClick: () => {},
  existingUserResponse: {},
  emailAddressFromSS: '',
  isExistingUserFromSS: false,
  setLoginSelectionOption: () => {},
  selectedPassResetOption: '',
  isV2Flow: false,
  otpMessageThreshold: 5000,
};

export default withStyles(LoginSection, styles);
export { LoginSection as LoginSectionVanilla };
